import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Hash, FileText, Users } from 'lucide-react';
import { Skeleton } from './ui/skeleton';
import { Link } from 'react-router-dom';
import { BASE_URL_UI, DummyCampaignImage } from '../../../../../../config/config';
import { CONTENT_TYPE_COLORS, formatBoth, formatNumberIN, getTierBadgeStyle, TIER_COLORS } from './ui/utils';
import { BorderStyle } from '@mui/icons-material';
import { NoData } from './ui/noData';
import { DarkTooltip } from './ui/darkTooltip';
import NumberWithTooltip from './ui/NumberWithTooltip';
import { Tooltip } from '@mui/material';
import { usePdfMode, pdfText } from './pdf/PdfModeContext';

interface postFilterDataProps {
  shortcode: string,
  gcp_display_url: string,
  full_name: string,
  __typename: string,
}

interface TopPerformersProps {
  platform: string;
  onTabChange?: (value: string) => void;
  topPerformerData: any;
  loading: boolean;
}


const colors = TIER_COLORS;

//  const TIER_COLORS: Record<string, string> = {
//     "Nano": "#ffb1b1",
//     "Micro": "#8bbfff",
//     "Micro Plus": "#1773e5",
//     "Macro": "#fcd34D",
//     "Macro Plus": "#c39500",
//     "Mega": "#84f4c1",
//     "Mega Plus": "#25a76c"
//   };
// const getTierBadgeStyle = (tier: string) => {
//   const color = colors[tier];
//   return {
//     backgroundColor: color + "33", // 20% opacity
//     borderColor: color,
//     color: color,
//     borderWidth: "1px",
//     BorderStyle:'solid'
//   };
// };
export function PostSkeleton() {
  return (
    <div className="flex items-center gap-4 p-4 rounded-lg border">
      <Skeleton className="w-8 h-8 rounded-full" />
      <Skeleton className="w-16 h-16 rounded-lg" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-1/3" />
      </div>
      <div className="text-right">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-4 w-10 mt-2" />
      </div>
    </div>
  );
}

export function InfluencerSkeleton() {
  return (
    <div className="flex items-center gap-4 p-4 rounded-lg border">
      <Skeleton className="w-8 h-8 rounded-full" />
      <Skeleton className="w-16 h-16 rounded-full" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
      <div className="text-right">
        <Skeleton className="h-4 w-12" />
        <Skeleton className="h-4 w-10 mt-2" />
      </div>
    </div>
  );
}

const topHashtags = [
  { hashtag: '#skincare', uses: 245, er: 6.8 },
  { hashtag: '#beauty', uses: 198, er: 6.2 },
  { hashtag: '#makeup', uses: 167, er: 5.9 },
  { hashtag: '#fashion', uses: 142, er: 5.5 },
  { hashtag: '#wellness', uses: 128, er: 5.3 }
];

const topPosts = [
  {
    id: 1,
    title: 'Summer Skincare Routine 2024',
    influencer: 'Sarah Johnson',
    platform: 'Instagram',
    views: 124500,
    engagement: 12450,
    er: 10.0,
    type: 'Reel',
    image: 'https://images.unsplash.com/photo-1614159102625-41ceafda13e9?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=200'
  },
  {
    id: 2,
    title: 'Unboxing New Beauty Products',
    influencer: 'Emma Williams',
    platform: 'YouTube',
    views: 98700,
    engagement: 8876,
    er: 9.0,
    type: 'Video',
    image: 'https://images.unsplash.com/photo-1585049303349-6680e6179692?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=200'
  },
  {
    id: 3,
    title: 'Morning Skincare Tips',
    influencer: 'Lisa Chen',
    platform: 'Instagram',
    views: 87600,
    engagement: 7446,
    er: 8.5,
    type: 'Reel',
    image: 'https://images.unsplash.com/photo-1656918129565-2f5a20ebab4d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=200'
  },
  {
    id: 4,
    title: 'Product Review: Natural Moisturizer',
    influencer: 'Michael Brown',
    platform: 'Facebook',
    views: 76500,
    engagement: 6120,
    er: 8.0,
    type: 'Post',
    image: 'https://images.unsplash.com/photo-1614159102625-41ceafda13e9?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=200'
  },
  {
    id: 5,
    title: 'Makeup Tutorial - Natural Look',
    influencer: 'Jessica Martinez',
    platform: 'Instagram',
    views: 69800,
    engagement: 5235,
    er: 7.5,
    type: 'Reel',
    image: 'https://images.unsplash.com/photo-1585049303349-6680e6179692?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=200'
  }
];

const topInfluencers = [
  {
    name: 'Sarah Johnson',
    avatar: 'SJ',
    posts: 24,
    views: 456789,
    engagement: 38976,
    er: 8.5,
    type: 'Mega',
    image: 'https://images.unsplash.com/photo-1678730939174-bf600a5e0af2?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=200'
  },
  {
    name: 'Emma Williams',
    avatar: 'EW',
    posts: 18,
    views: 334567,
    engagement: 28436,
    er: 8.5,
    type: 'Macro',
    image: 'https://images.unsplash.com/photo-1515020670732-a82cf924258e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=200'
  },
  {
    name: 'Lisa Chen',
    avatar: 'LC',
    posts: 32,
    views: 298765,
    engagement: 23901,
    er: 8.0,
    type: 'Macro',
    image: 'https://images.unsplash.com/photo-1678730939174-bf600a5e0af2?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=200'
  },
  {
    name: 'Michael Brown',
    avatar: 'MB',
    posts: 15,
    views: 245678,
    engagement: 18426,
    er: 7.5,
    type: 'Micro',
    image: 'https://images.unsplash.com/photo-1515020670732-a82cf924258e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=200'
  },
  {
    name: 'Jessica Martinez',
    avatar: 'JM',
    posts: 21,
    views: 198432,
    engagement: 14882,
    er: 7.5,
    type: 'Micro',
    image: 'https://images.unsplash.com/photo-1678730939174-bf600a5e0af2?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=200'
  }
];

export function TopPerformers({ platform, onTabChange, topPerformerData, loading }: TopPerformersProps) {
  // PDF: titles printed (shortened) instead of cut off behind a tooltip
  const pdfMode = usePdfMode();
  // PDF: no outer box or side padding -- the item cards carry the borders
  const pdfList = pdfMode ? { className: 'border-0 shadow-none', 'data-pdf-noframe': '' } : {};
  const pdfPad = pdfMode ? 'px-0' : undefined;
  const getPlatformColor = (platform: string) => {
    switch (platform) {
      case 'Instagram':
        return 'bg-pink-100 text-pink-700';
      case 'Facebook':
        return 'bg-blue-100 text-blue-700';
      case 'YouTube':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card {...pdfList}>
        <CardHeader className={pdfPad}>
          <CardTitle>Top Performing Posts</CardTitle>
          {/* <CardDescription>Highest performing content</CardDescription> */}
        </CardHeader>
        <CardContent className={pdfPad} style={pdfMode ? undefined : {maxHeight:'940px', overflowY:'auto'}}>


          <div className="space-y-4">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => <InfluencerSkeleton key={i} />)
            ) : (
              topPerformerData?.posts?.length > 0 && topPerformerData?.posts?.map((post: any, index: any) => (
                <div key={post.id} data-pdf-row className="flex items-start sm:items-center gap-2 sm:gap-4 p-3 rounded-lg border hover:bg-slate-50 transition-colors">
                  <div className='flex gap-2'>
                    <div className="text-[12px] flex items-center justify-center sm:w-8 sm:h-8 w-5 h-5 rounded-full bg-slate-100 text-slate-700 shrink-0">
                      {index + 1}
                    </div>
                    
                    <Link className=' w-8 h-8 sm:min-w-[50px] d-inline-flex' to={`${post.platform === 'facebook' ? post.user_url : `${BASE_URL_UI}/brand/profile-insight?sm_id=${post.profile_id}&influencerId=${post.influencer_id}`}`} target='_blank'>
                      <img
                        src={post.image || DummyCampaignImage.default_campaign_image}
                        alt={post.title}
                        className="sm:w-10 sm:h-10 w-8 h-8 max-w-[100%] rounded-lg object-cover shrink-0 mr-2"
                      />
                    </Link>
                  </div>
                  {/* OLD: fixed width - rows with a short title stayed at this width while long
                      titles stretched past it, so views/ER didn't line up across rows */}
                  {/* <div className='flex flex-col sm:flex-row w-[calc(100%_-_4rem)] sm:w-[calc(100%_-_12rem)]'>
                    <div className="flex items-start gap-3 w-[calc(100%_-_1rem)]"> */}
                  <div className='flex flex-col sm:flex-row flex-1 min-w-0'>
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      {/* LEFT: title + by */}
                      <div className="flex-1 min-w-0 w-[calc(100%_-_6rem)]">
                        <p className={`text-slate-900 ${pdfMode ? '' : 'truncate'} text-[14px] sm:text-[16px] w-[calc(100%_-_1rem)]`}>
                          <Link to={`${post.url}`} target="_blank">
                            <DarkTooltip title={post.title} placement="top-start" arrow>
                              {/* {post.title} */}
                              <span>{pdfMode ? pdfText(post.title, 60) : post.title}</span>
                            </DarkTooltip>
                          </Link>
                        </p>

                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <span>by</span>
                          <Link
                            className={pdfMode ? '' : 'truncate'}
                            to={post.platform === 'facebook'
                              ? `${BASE_URL_UI}/brand/profile-insight-fb?profile_id=${post.profile_id || post.url_id}`
                              : post.platform === 'youtube'
                              ? `${BASE_URL_UI}/brand/profile-insight-yt?sm_id=${post.profile_id}&influencerId=${post.influencer_id}`
                              : `${BASE_URL_UI}/brand/profile-insight?sm_id=${post.profile_id}&influencerId=${post.influencer_id}`
                            }
                            target="_blank"
                          >
                            {post?.influencer}
                          </Link>
                        </div>
                      </div>

                      {/* MIDDLE: Reel badge */}
                      <span
                        className="hidden sm:flex shrink-0"
                        style={getTierBadgeStyle(post.type, CONTENT_TYPE_COLORS)}
                      >
                        {post.type}
                      </span>
                    </div>
                    {/* OLD: <div className="sm:text-right shrink-0 md:ms-2 flex-1 min-w-[96px]"> */}
                    <div className="sm:text-right shrink-0 md:ms-2 min-w-[96px]">
                        <p className="text-slate-900">
                        <NumberWithTooltip value={post.views} tooltipComponent={Tooltip} />  <span style={{ fontSize: '13px', color: '#5d5d5d' }}>views</span></p>
                        <p className="text-sm text-green-600">{post.er}% ER</p>
                        <span
                        className="shrink-0 sm:hidden"
                        style={getTierBadgeStyle(post.type, CONTENT_TYPE_COLORS)}
                      >
                        {post.type}
                      </span> 
                      </div>
                    </div>
                  {/* <div className='flex flex-col md:flex-row w-full'>
                    <div className="flex-col sm:flex-row sm:flex-1 sm:min-w-0">
                      <div className="flex items-center justify-between gap-2 w-full">
                        <p className="text-slate-900 truncate max-w-[60%] sm:max-w-[50%] text-[14px] sm:text-[16px]">

                          <Link to={`${post.url}`} target='_blank'>
                            <DarkTooltip title={post.title} placement="top-start" arrow>
                              {post.title}
                            </DarkTooltip>
                          </Link>

                        </p>
                        <span
                          style={getTierBadgeStyle(post.type, CONTENT_TYPE_COLORS)}>
                          {post.type}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <span>by 
                          
                          <Link style={{paddingLeft:'4px'}} to={
                          post.platform === 'facebook'
                            ? `${BASE_URL_UI}/brand/profile-insight-fb?profile_id=${post.profile_id || post.url_id}`
                            : post.platform === 'youtube'
                            ? `${BASE_URL_UI}/brand/profile-insight-yt?sm_id=${post.profile_id}&influencerId=${post.influencer_id}`
                            : `${BASE_URL_UI}/brand/profile-insight?sm_id=${post.profile_id}&influencerId=${post.influencer_id}`
                        }
                        target="_blank"
                      >
                         
                          {post?.influencer}</Link></span>
                       
                      </div>
                    </div>
                    <div className="sm:text-right shrink-0">
                      <p className="text-slate-900">
                      <NumberWithTooltip value={post.views} tooltipComponent={Tooltip} />  <span style={{ fontSize: '13px', color: '#5d5d5d' }}>views</span></p>
                      <p className="text-sm text-green-600">{post.er}% ER</p>
                    </div>
                  </div> */}
                </div>
              ))
            )}
            {topPerformerData?.posts?.length === 0 && !loading && (
              <NoData message="No top performing posts available for the selected range" />
            )}
          </div>

        </CardContent>
      </Card>
      <Card {...pdfList}>
        <CardHeader className={pdfPad}>
          <CardTitle>Top Performing Influencers</CardTitle>
          {/* <CardDescription>Highest performing creators</CardDescription> */}
        </CardHeader>
        <CardContent className={pdfPad} style={pdfMode ? undefined : {maxHeight: '566px',overflowY: 'auto'}}>
          <div className="space-y-4">
            {loading ? (
                Array.from({ length: 4 }).map((_, i) => <InfluencerSkeleton key={i} />)
              ) : topPerformerData?.influencers?.length > 0 && topPerformerData?.influencers?.map((influencer: any, index: any) => (
                <div key={index} data-pdf-row className="flex items-start gap-2 sm:gap-4 p-3 rounded-lg border hover:bg-slate-50 transition-colors">
                  <div className='flex items-start gap-2'>
                    <div className="text-[12px] flex items-center justify-center sm:w-8 sm:h-8 w-5 h-5 rounded-full bg-slate-100 text-slate-700 shrink-0">
                      {index + 1}
                    </div>
                    <Avatar className="h-10 w-10 z-index-0">
                      <Link to={
                          influencer.platform === 'facebook'
                            ? `${BASE_URL_UI}/brand/profile-insight-fb?profile_id=${influencer.profile_id || influencer.url_id}`
                            : influencer.platform === 'youtube'
                            ? `${BASE_URL_UI}/brand/profile-insight-yt?sm_id=${influencer.profile_id}&influencerId=${influencer.influencer_id}`
                            : `${BASE_URL_UI}/brand/profile-insight?sm_id=${influencer.profile_id}&influencerId=${influencer.influencer_id}`
                        }
                        target="_blank"
                      >
                        <AvatarImage src={influencer.image} alt={influencer.name} />
                        
                        </Link>
                      <AvatarFallback>{influencer.avatar}</AvatarFallback>
                    </Avatar>
                  </div>
                  {/* OLD: tier badge sat right after the name, so its position depended on the name
                      length (and long names wrapped, pushing it into the stats column). Now laid out
                      like Top Performing Posts: name/posts | badge column | interactions/ER column. */}
                  {/*
                  <div className='flex flex-col sm:flex-row flex-1'>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Link to={
                            influencer.platform === 'facebook'
                              ? `${BASE_URL_UI}/brand/profile-insight-fb?profile_id=${influencer.profile_id || influencer.url_id}`
                              : influencer.platform === 'youtube'
                              ? `${BASE_URL_UI}/brand/profile-insight-yt?sm_id=${influencer.profile_id}&influencerId=${influencer.influencer_id}`
                              : `${BASE_URL_UI}/brand/profile-insight?sm_id=${influencer.profile_id}&influencerId=${influencer.influencer_id}`
                          }
                          target="_blank"
                        >
                        <p className="text-slate-900">{influencer.name}</p></Link>

                        <span  className="hidden sm:flex shrink-0" style={getTierBadgeStyle(influencer.type, TIER_COLORS)}>
                          {influencer.type}
                        </span>
                      </div>
                      
                      <p className="text-sm text-slate-600">
                        {formatNumberIN(influencer.posts)}{" "}
                        {influencer.posts === 1 ? "post" : "posts"} •{" "}
                        <NumberWithTooltip value={influencer.views} unit="views" showBoth={false} tooltipComponent={Tooltip} />
                      </p>
                    </div>
                    <div className="sm:text-right shrink-0" >
                  */}
                  <div className='flex flex-col sm:flex-row flex-1 min-w-0'>
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      {/* LEFT: name + posts/views */}
                      <div className="flex-1 min-w-0">
                        <Link
                          className={`d-block ${pdfMode ? '' : 'truncate'}`}
                          to={
                            influencer.platform === 'facebook'
                              ? `${BASE_URL_UI}/brand/profile-insight-fb?profile_id=${influencer.profile_id || influencer.url_id}`
                              : influencer.platform === 'youtube'
                              ? `${BASE_URL_UI}/brand/profile-insight-yt?sm_id=${influencer.profile_id}&influencerId=${influencer.influencer_id}`
                              : `${BASE_URL_UI}/brand/profile-insight?sm_id=${influencer.profile_id}&influencerId=${influencer.influencer_id}`
                          }
                          target="_blank"
                          title={influencer.name}
                        >
                          <span className="text-slate-900">{influencer.name}</span>
                        </Link>

                        <p className="text-sm text-slate-600">
                          {formatNumberIN(influencer.posts)}{" "}
                          {influencer.posts === 1 ? "post" : "posts"} •{" "}
                          <NumberWithTooltip value={influencer.views} unit="views" showBoth={false} tooltipComponent={Tooltip} />
                        </p>
                      </div>

                      {/* MIDDLE: tier badge */}
                      <span className="hidden sm:flex shrink-0" style={getTierBadgeStyle(influencer.type, TIER_COLORS)}>
                        {influencer.type}
                      </span>
                    </div>
                    <div className="sm:text-right shrink-0 md:ms-2 min-w-[96px]" >
                      <p className="text-slate-900">
                        <NumberWithTooltip value={influencer.engagement} tooltipComponent={Tooltip} />
                        <span style={{ fontSize: '13px', color: '#5d5d5d' }}> Total Interactions</span></p>
                      <p className="text-sm text-green-600">{influencer.er}% ER</p>
                       <span className='shrink-0 sm:hidden' style={getTierBadgeStyle(influencer.type, TIER_COLORS)}>
                          {influencer.type}
                        </span>
                    </div>
                  </div>
                </div>
              )
            )}
            {topPerformerData?.influencers?.length === 0 && !loading && (
              <NoData message="No top performing influencers available for the selected range" />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
